import { Component } from '@theme/component';
import { DESTINATION_STATE_CHANGE, getDestinationState } from '@theme/destination-state';

/**
 * Event dispatched (bubbling) on the component when the shopper clicks the
 * desktop "Change state" button in the "Ships to you" card. The header
 * component listens for it and opens its state picker; this component does
 * nothing else with it.
 *
 * @type {string}
 */
export const HEADER_STATE_PANEL_CHANGE_STATE = 'header-state-panel:change-state';

const COUNT_PLACEHOLDER = '{count}';

/**
 * "Shop by State" panel content (snippets/site-header-panel-state.liquid).
 *
 * Keeps the "Ships to you" card's ranch count in sync with the site-wide
 * destination state: on `destination-state:change` it reads the count from
 * the panel's own state rows (`.js-header-state-panel-row` with
 * `data-state-code` / `data-ranch-count`; a state without a row counts 0),
 * fills `.js-header-state-panel-count` from its `data-copy-one` /
 * `data-copy-many` templates and toggles `hidden` on the served / empty
 * blocks. The card's state name, link href and aria-label are NOT handled
 * here — assets/destination-state.js keeps those live through its
 * `data-destination-*` hooks.
 *
 * The desktop layout also has a "Change state" button
 * (`.js-header-state-panel-change`) that asks the header for its picker; the
 * mobile layout instead has a native popover (`.js-header-state-panel-picker`,
 * opened by `popovertarget`), so both are optional. When that popover opens,
 * focus moves to the selected option (else the first) without scrolling.
 */
class HeaderStatePanel extends Component {
  /** @type {HTMLElement | null} */
  #served = null;

  /** @type {HTMLElement | null} */
  #empty = null;

  /** @type {HTMLElement | null} */
  #count = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    // Markup contract — resolved once; a missing hook is a template bug.
    this.#served = this.#requireHook('.js-header-state-panel-served');
    this.#empty = this.#requireHook('.js-header-state-panel-empty');
    this.#count = this.#requireHook('.js-header-state-panel-count');

    // A fresh controller per connection, so the component survives the
    // disconnect / reconnect cycle of the theme editor.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(DESTINATION_STATE_CHANGE, this.#updateShipsCardOnDestinationChange, { signal });

    const changeButton = this.querySelector('.js-header-state-panel-change');
    changeButton?.addEventListener('click', this.#requestStatePickerOnChangeClick, { signal });

    const picker = this.querySelector('.js-header-state-panel-picker');
    picker?.addEventListener('toggle', this.#focusSelectedOptionOnPickerOpen, { signal });

    // The server rendered the card for the same state the store resolves to;
    // re-rendering here only matters after a morph / reconnect.
    const code = getDestinationState();
    if (code) this.#renderShipsCard(code);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Resolves one element of the component's own markup contract.
   *
   * @param {string} selector
   * @returns {HTMLElement}
   */
  #requireHook(selector) {
    const element = this.querySelector(selector);
    if (!(element instanceof HTMLElement)) {
      throw new Error(`header-state-panel: missing ${selector}`);
    }
    return element;
  }

  /**
   * Re-renders the card's count and served / empty blocks for the new
   * destination state (`event.detail` is `{ code, previous, state }`).
   *
   * @param {Event} event
   */
  #updateShipsCardOnDestinationChange = (event) => {
    const code = event instanceof CustomEvent ? event.detail?.code : undefined;
    if (typeof code !== 'string' || !code) return;
    this.#renderShipsCard(code);
  };

  /**
   * Asks the header to open its state picker.
   */
  #requestStatePickerOnChangeClick = () => {
    this.dispatchEvent(new CustomEvent(HEADER_STATE_PANEL_CHANGE_STATE, { bubbles: true }));
  };

  /**
   * Moves focus into the mobile picker popover once it opens — to the
   * selected option, else the first — without scrolling the list.
   *
   * @param {Event} event
   */
  #focusSelectedOptionOnPickerOpen = (event) => {
    const { newState } = /** @type {ToggleEvent} */ (event);
    const picker = event.currentTarget;
    if (newState !== 'open' || !(picker instanceof HTMLElement)) return;

    const target =
      picker.querySelector('.state-picker__option._selected, .state-picker__option[aria-current="true"]') ??
      picker.querySelector('.state-picker__option');

    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  };

  /**
   * Ranch count rendered for a state in this panel's rows; 0 when the state
   * has no row or the attribute is not a number.
   *
   * @param {string} code - Two-letter state code.
   * @returns {number}
   */
  #ranchCountFor(code) {
    const row = this.querySelector(`.js-header-state-panel-row[data-state-code="${CSS.escape(code)}"]`);
    if (!row) return 0;

    const count = Number.parseInt(row.getAttribute('data-ranch-count') ?? '', 10);
    return Number.isNaN(count) ? 0 : count;
  }

  /**
   * @param {string} code - Two-letter state code.
   */
  #renderShipsCard(code) {
    const { served, empty, count: countElement } = { served: this.#served, empty: this.#empty, count: this.#count };
    if (!served || !empty || !countElement) return;

    const count = this.#ranchCountFor(code);
    const template = countElement.getAttribute(count === 1 ? 'data-copy-one' : 'data-copy-many') ?? '';
    const text = template.split(COUNT_PLACEHOLDER).join(String(count));

    if (countElement.textContent !== text) countElement.textContent = text;
    served.hidden = count === 0;
    empty.hidden = count > 0;
  }
}

if (!customElements.get('header-state-panel')) {
  customElements.define('header-state-panel', HeaderStatePanel);
}
